import React, { Suspense } from 'react';
import { WAREHOUSE_AREA_PERMISSIONS } from '@easyflow/shared';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams, useLocation, matchPath } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StorageQuotaErrorToastProvider } from '@easyflow/ui-frontend';
import { Toaster } from '@easyflow/ui-frontend/components/ui/toaster';
import { TooltipProvider } from '@easyflow/ui-frontend/components/ui/tooltip';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SocketProvider } from '@/contexts/SocketContext';
import { SipPhoneProvider } from '@/contexts/SipPhoneContext';
import { ModuleProvider } from '@/contexts/ModuleContext';
import { PermissionProvider } from '@/contexts/PermissionContext';
import { TenantLimitErrorModal } from '@/components/limits/TenantLimitErrorModal';
import { ShortcutsHelpProvider } from '@/components/aurora/ShortcutsHelp';
import Layout from '@/components/layout/Layout';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import { NotificationHandler } from '@/components/NotificationHandler';
import Login from '@/pages/auth/Login';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import TenantNotFound from '@/pages/auth/TenantNotFound';
import { useTenantValidation } from '@/hooks/useTenantValidation';
import DashboardRouter from '@/pages/dashboard/DashboardRouter';
import Switchboard from '@/pages/voip/Switchboard';
import Tenants from '@/pages/tenant/Tenants';
import Stores from '@/pages/tenant/Stores';
import Users from '@/pages/tenant/Users';
import Extensions from '@/pages/voip/Extensions';
import CDRViewer from '@/pages/voip/CDRViewer';
import LiveCalls from '@/pages/voip/LiveCalls';
import TrunkRegistration from '@/pages/voip/TrunkRegistration';
import CallRouting from '@/pages/voip/CallRouting';
import DialplanEditor from '@/pages/voip/DialplanEditor';
import DialplanContextManager from '@/pages/voip/DialplanContextManager';
import TenantContextEditor from '@/pages/voip/TenantContextEditor';
import DialplanDebug from '@/pages/voip/DialplanDebug';
import SipTrunks from '@/pages/voip/SipTrunks';
import Destinations from '@/pages/voip/Destinations';
import Logs from '@/pages/voip/Logs';
import RingGroups from '@/pages/voip/RingGroups';
import Queues from '@/pages/voip/Queues';
import QueueDashboard from '@/pages/voip/QueueDashboard';
import IvrMenus from '@/pages/voip/IvrMenus';
import AudioManager from '@/pages/voip/AudioManager';
import ConferenceRooms from '@/pages/voip/ConferenceRooms';
import Voicemail from '@/pages/voip/Voicemail';
import TimeConditions from '@/pages/voip/TimeConditions';
import HolidayCalendars from '@/pages/voip/HolidayCalendars';
import { DeveloperSettings } from '@/pages/impostazioni/DeveloperSettings';
import KBMetadataSchemaPage from '@/pages/admin/KBMetadataSchemaPage';
import WorkflowBuilder from '@/pages/ai/WorkflowBuilder';
import Settings from '@/pages/impostazioni/Settings';
import Analytics from '@/pages/dashboard/Analytics';
import AITools from '@/pages/ai/AITools';
import LandingPageBuilder from '@/components/ai/LandingPageBuilder';
import WakeupCalls from '@/pages/voip/WakeupCalls';
import RagioneSociale from '@/pages/tenant/RagioneSociale';
import { MessagingChat, MessagingAnalytics, MessagingBilling, MessagingDocs } from '@/pages/messaging/index';
import CommunicationSettings from '@/pages/messaging/CommunicationSettings';
import { MessengerChatPage, WorkgroupsListPage, WorkgroupHub, ChannelsPage, MessengerLayout } from '@/pages/messenger/index';
import VerifyEmail from '@/pages/auth/VerifyEmail';
import AcceptInvite from '@/pages/auth/AcceptInvite';
import ImpersonateHandler from '@/pages/auth/ImpersonateHandler';
import CRM from '@/pages/crm/CRM';
import Email from '@/pages/messaging/Email';
import Calendar from '@/pages/calendar/Calendar';
import CalendarSettings from '@/pages/calendar/CalendarSettings';
import KnowledgeBasePage from '@/pages/ai/KnowledgeBasePage';
import ChatbotWidget from '@/pages/ai/ChatbotWidget';
import SupportTickets from '@/pages/tasks/SupportTickets';
import Tasks from '@/pages/tasks/Tasks';
import RoutinesEntry from '@/pages/ai/RoutinesEntry';
import BookingManage from '@/pages/booking/BookingManage';
import BookingPreview from '@/pages/booking/BookingPreview';

import VideoCall from '@/pages/video/VideoCall';
import VideoRoomsHistory from '@/pages/video/VideoRoomsHistory';
import VideoGuestRedirect from '@/pages/video/VideoGuestRedirect';
import FileManager from '@/pages/files/FileManager';
import HRPage from '@/pages/hr/HRPage';
import FeedPage from '@/pages/feed/FeedPage';
import HRQrClockIn from '@/pages/hr/HRQrClockIn';
import VoipPbxManagement from '@/pages/voip/VoipPbxManagement';
import TenantManagementPage from '@/pages/tenant/TenantManagementPage';
const FeedTopicsAdminPage = React.lazy(() => import('@/pages/feed-admin/FeedTopicsAdminPage'));
const FeedMilestonesAdminPage = React.lazy(() => import('@/pages/feed-admin/FeedMilestonesAdminPage'));
const FeedModerationPage = React.lazy(() => import('@/pages/feed-admin/FeedModerationPage'));
const FeedAuditPage = React.lazy(() => import('@/pages/feed-admin/FeedAuditPage'));
const CompanyWiki = React.lazy(() => import('@/pages/wiki/CompanyWiki'));
const CassaPage = React.lazy(() => import('@/pages/pos/CassaPage'));
const CatalogoPage = React.lazy(() => import('@/pages/pos/CatalogoPage'));
const AmministrazionePage = React.lazy(() => import('@/pages/pos/AmministrazionePage'));
const LogisticsHub = React.lazy(() => import('@/pages/pos/LogisticsHub'));
// Task #3405 — hub Incentivazione (commissioning/gare), gated modulo+permesso.
const IncentivazionePage = React.lazy(() => import('@/pages/incentivazione/IncentivazionePage'));
const InvoiceDetailPage = React.lazy(() => import('@/pages/pos/InvoiceDetailPage'));
const POSStandalone = React.lazy(() => import('@/pages/pos/POSStandalone'));
const POSLauncher = React.lazy(() => import('@/pages/pos/POSLauncher'));

function RedirectToTab({ base, tab, extraParams }: { base: string; tab: string; extraParams?: Record<string, string> }) {
  const [searchParams] = useSearchParams();
  const { tenantSlug } = useParams();
  const newParams = new URLSearchParams(searchParams);
  newParams.set('tab', tab);
  if (extraParams) {
    for (const [k, v] of Object.entries(extraParams)) newParams.set(k, v);
  }
  return <Navigate to={`/${tenantSlug}/${base}?${newParams.toString()}`} replace />;
}

function RedirectToCalendarSettings({ section }: { section: string }) {
  const [searchParams] = useSearchParams();
  const { tenantSlug } = useParams();
  const newParams = new URLSearchParams(searchParams);
  newParams.set('section', section);
  newParams.delete('tab');
  return <Navigate to={`/${tenantSlug}/calendar/settings?${newParams.toString()}`} replace />;
}

// Task #1046 — `warehouses` e `movements` non sono più tab del Catalogo:
// vivono nel LogisticsHub. Vengono gestite separatamente sotto.
const WAREHOUSE_TAB_MAP: Record<string, string> = {
  products: 'articoli',
  stock: 'magazzino',
  suppliers: 'fornitori',
  brands: 'brand',
  categories: 'categorie',
  units: 'unita',
};

function RedirectWarehouse() {
  const [searchParams] = useSearchParams();
  const { tenantSlug } = useParams();
  const oldTab = searchParams.get('tab');
  // Task #1046: vecchi link /warehouse?tab=warehouses|movements → LogisticsHub
  if (oldTab === 'warehouses') {
    return <Navigate to={`/${tenantSlug}/amministrazione/logistica?tab=magazzini`} replace />;
  }
  if (oldTab === 'movements') {
    return <Navigate to={`/${tenantSlug}/amministrazione/logistica?tab=eventi-logistici`} replace />;
  }
  const newTab = (oldTab && WAREHOUSE_TAB_MAP[oldTab]) || 'articoli';
  return <Navigate to={`/${tenantSlug}/catalogo?tab=${newTab}`} replace />;
}

// Create a client outside component to persist across re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function TenantRoutes() {
  const { tenantSlug } = useParams();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { isValid: tenantValid, isLoading: tenantValidating } = useTenantValidation(tenantSlug);

  if (authLoading || tenantValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Slug non valido (tenant non esiste): mostra TenantNotFound, non il form di login
  if (tenantValid === false) {
    return <TenantNotFound />;
  }

  return (
    <Routes>
      <Route path="login" element={<Login tenantSlug={tenantSlug || ''} />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-password" element={<ResetPassword />} />
      <Route path="verify-email" element={<VerifyEmail />} />
      <Route path="accept-invite" element={<AcceptInvite />} />
      <Route path="auth/impersonate" element={<ImpersonateHandler />} />
      <Route path="video/:roomId" element={<VideoCall />} />
      <Route path="hr/qr-clockin" element={<HRQrClockIn />} />
      {isAuthenticated ? (
        <>
        <Route path="punto-cassa" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}>
            <POSStandalone />
          </Suspense>
        } />
        <Route path="/*" element={
          <ErrorBoundary tenantSlug={tenantSlug}>
          <SipPhoneProvider>
          <Layout>
            <ErrorBoundary tenantSlug={tenantSlug}>
            <Routes>
              <Route path="dashboard" element={<ProtectedRoute permission="dashboard.view"><DashboardRouter /></ProtectedRoute>} />
              <Route path="pbx" element={<ProtectedRoute permission="pbx.manage"><VoipPbxManagement /></ProtectedRoute>} />
              <Route path="tenant-management" element={<ProtectedRoute permission="users.view"><TenantManagementPage /></ProtectedRoute>} />
              <Route path="switchboard" element={<ProtectedRoute permission="switchboard.view"><Switchboard /></ProtectedRoute>} />
              <Route path="tenants" element={<Tenants />} />
              <Route path="cdr" element={<ProtectedRoute permission="cdr.view"><CDRViewer /></ProtectedRoute>} />
              <Route path="live-calls" element={<ProtectedRoute permission="live_calls.view"><LiveCalls /></ProtectedRoute>} />
              <Route path="trunk-registration" element={<ProtectedRoute permission="trunk.view"><TrunkRegistration /></ProtectedRoute>} />
              <Route path="dialplan-editor" element={<ProtectedRoute permission="pbx.manage"><DialplanEditor /></ProtectedRoute>} />
              <Route path="dialplan-context" element={<ProtectedRoute permission="pbx.manage"><DialplanContextManager /></ProtectedRoute>} />
              <Route path="context-editor" element={<ProtectedRoute permission="pbx.manage"><TenantContextEditor /></ProtectedRoute>} />
              <Route path="destinations" element={<ProtectedRoute permission="routing.view"><Destinations /></ProtectedRoute>} />
              <Route path="logs" element={<ProtectedRoute permission="logs.view"><Logs /></ProtectedRoute>} />
              <Route path="queue-dashboard" element={<ProtectedRoute permission="queues.view"><QueueDashboard /></ProtectedRoute>} />
              <Route path="holiday-calendars" element={<ProtectedRoute permission="pbx.manage"><HolidayCalendars /></ProtectedRoute>} />
              <Route path="developer" element={<ProtectedRoute permission="integrations.view"><DeveloperSettings /></ProtectedRoute>} />
              <Route path="admin/kb-metadata-schema" element={<ProtectedRoute permission="integrations.view"><KBMetadataSchemaPage /></ProtectedRoute>} />
              <Route path="workflow-builder" element={<ProtectedRoute permission="integrations.view"><WorkflowBuilder /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute permission="settings.manage"><Settings /></ProtectedRoute>} />
              <Route path="analytics" element={<ProtectedRoute permission="analytics.view"><Analytics /></ProtectedRoute>} />
              <Route path="extensions" element={<RedirectToTab base="pbx" tab="extensions" />} />
              <Route path="sip-trunks" element={<RedirectToTab base="pbx" tab="sip-trunks" />} />
              <Route path="call-routing" element={<RedirectToTab base="pbx" tab="call-routing" />} />
              <Route path="dialplan-debug" element={<RedirectToTab base="pbx" tab="dialplan-debug" />} />
              <Route path="ring-groups" element={<RedirectToTab base="pbx" tab="ring-groups" />} />
              <Route path="queues" element={<RedirectToTab base="pbx" tab="queues" />} />
              <Route path="ivr-menus" element={<RedirectToTab base="pbx" tab="ivr-menus" />} />
              <Route path="audio-manager" element={<RedirectToTab base="pbx" tab="audio-manager" />} />
              <Route path="conference-rooms" element={<RedirectToTab base="pbx" tab="conference-rooms" />} />
              <Route path="voicemail" element={<RedirectToTab base="pbx" tab="voicemail" />} />
              <Route path="time-conditions" element={<RedirectToTab base="pbx" tab="time-conditions" />} />
              <Route path="ragione-sociale" element={<RedirectToTab base="tenant-management" tab="ragione-sociale" />} />
              <Route path="stores" element={<RedirectToTab base="tenant-management" tab="sedi" />} />
              <Route path="users" element={<RedirectToTab base="tenant-management" tab="users" />} />
              <Route path="ai-tools" element={<ProtectedRoute permission="ai_tools.access"><AITools /></ProtectedRoute>} />
              <Route path="web-builder" element={<ProtectedRoute permission="site_builder.access"><LandingPageBuilder /></ProtectedRoute>} />
              {/* Legacy alias kept permanently to preserve old saved links */}
              <Route path="site-builder" element={<ProtectedRoute permission="site_builder.access"><LandingPageBuilder /></ProtectedRoute>} />
              <Route path="wakeup-calls" element={<ProtectedRoute permission="wakeup_calls.manage"><WakeupCalls /></ProtectedRoute>} />
              <Route path="messaging/feed" element={<ProtectedRoute permission="messenger.access"><FeedPage /></ProtectedRoute>} />
              <Route path="messaging/chat" element={<ProtectedRoute permission="messenger.access"><MessagingChat /></ProtectedRoute>} />
              <Route path="messaging/analytics" element={<ProtectedRoute permission="messenger.access"><MessagingAnalytics /></ProtectedRoute>} />
              <Route path="messaging/billing" element={<ProtectedRoute permission="messenger.access"><MessagingBilling /></ProtectedRoute>} />
              <Route path="messaging/settings" element={<ProtectedRoute permission="communication.settings.manage"><CommunicationSettings /></ProtectedRoute>} />
              <Route path="messaging/docs" element={<ProtectedRoute permission="messenger.access"><MessagingDocs /></ProtectedRoute>} />
              <Route path="messaging/email" element={<ProtectedRoute permission="email.view"><Email /></ProtectedRoute>} />
              {/* Consolidato nel tab "Account Email" di Impostazioni Comunicazione; redirect per i link salvati */}
              <Route path="messaging/email-accounts" element={<RedirectToTab base="messaging/settings" tab="email" />} />
              <Route path="crm" element={<ProtectedRoute permission="crm.view"><CRM /></ProtectedRoute>} />
              <Route path="calendar" element={<ProtectedRoute permission="calendar.view"><Calendar /></ProtectedRoute>} />
              <Route path="calendar/settings" element={<ProtectedRoute permission="calendar.view"><CalendarSettings /></ProtectedRoute>} />
              <Route path="calendar/kb" element={<ProtectedRoute permission="calendar.view"><KnowledgeBasePage /></ProtectedRoute>} />
              <Route path="support" element={<ProtectedRoute permission="support.manage"><SupportTickets /></ProtectedRoute>} />
              <Route path="tasks" element={<ProtectedRoute permission="tasks.view"><Tasks /></ProtectedRoute>} />
              <Route path="routines" element={<RoutinesEntry />} />
              <Route path="appointment-forms" element={<RedirectToCalendarSettings section="forms" />} />

              <Route path="video-calls" element={<ProtectedRoute permission="video_call.access"><VideoRoomsHistory /></ProtectedRoute>} />
              <Route path="cassa" element={<ProtectedRoute permission="pos.cassa"><Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><CassaPage /></Suspense></ProtectedRoute>} />
              <Route path="catalogo" element={<ProtectedRoute permission="pos.catalogo"><Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><CatalogoPage /></Suspense></ProtectedRoute>} />
              <Route path="amministrazione" element={<ProtectedRoute permission="pos.amministrazione"><Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><AmministrazionePage /></Suspense></ProtectedRoute>} />
              <Route path="amministrazione/logistica" element={<ProtectedRoute permission={WAREHOUSE_AREA_PERMISSIONS}><Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><LogisticsHub /></Suspense></ProtectedRoute>} />
              <Route path="incentivazione" element={<ProtectedRoute permission="incentivazione.view" moduleKey="incentivazione"><Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><IncentivazionePage /></Suspense></ProtectedRoute>} />
              <Route path="pos" element={<RedirectToTab base="cassa" tab="punto-cassa" />} />
              <Route path="pos-settings" element={<RedirectToTab base="cassa" tab="impostazioni-cassa" />} />
              <Route path="pos/settings" element={<RedirectToTab base="cassa" tab="impostazioni" />} />
              <Route path="pos/fatture/:id" element={<ProtectedRoute permission="pos.amministrazione"><Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><InvoiceDetailPage /></Suspense></ProtectedRoute>} />
              <Route path="pos/fatture" element={<RedirectToTab base="amministrazione" tab="documenti-fiscali" />} />
              {/* Task #1466 — alias canonico nuova nomenclatura. Accetta
                  query string (`direction`, `invoice`, `new`, `category`)
                  e li ribalta sul tab unificato in AmministrazionePage. */}
              <Route path="pos/documenti-fiscali" element={<RedirectToTab base="amministrazione" tab="documenti-fiscali" />} />
              {/* Task #1466 — back-compat deep-link "Fatture Passive": stessa
                  pagina unificata, ma direzione pre-filtrata a "passiva". */}
              <Route path="pos/fatture-passive" element={<RedirectToTab base="amministrazione" tab="documenti-fiscali" extraParams={{ direction: 'passiva' }} />} />
              <Route path="pos/sales" element={<RedirectToTab base="amministrazione" tab="vendite" />} />
              <Route path="pos/purchase-orders" element={<RedirectToTab base="amministrazione/logistica" tab="ordini-fornitore" />} />
              <Route path="pos/ddt" element={<RedirectToTab base="amministrazione/logistica" tab="ddt" />} />
              <Route path="pos/ddt-passivi" element={<RedirectToTab base="amministrazione/logistica" tab="ddt" extraParams={{ cycle: 'passive' }} />} />
              <Route path="pos/transfers" element={<RedirectToTab base="amministrazione/logistica" tab="trasferimenti" />} />
              <Route path="pos/cash-book" element={<RedirectToTab base="amministrazione" tab="prima-nota" />} />
              <Route path="pos/daily-closings" element={<RedirectToTab base="amministrazione" tab="chiusure-fiscali" />} />
              <Route path="pos/receipts" element={<RedirectToTab base="amministrazione" tab="corrispettivi" />} />
              <Route path="pos/deadlines" element={<RedirectToTab base="amministrazione" tab="scadenzario" />} />
              <Route path="pos/reports" element={<RedirectToTab base="amministrazione" tab="report" />} />
              <Route path="pos/product-types" element={<RedirectToTab base="catalogo" tab="categorie" />} />
              <Route path="warehouse" element={<RedirectWarehouse />} />
              <Route path="price-lists" element={<RedirectToTab base="catalogo" tab="listini" />} />
              {/* Back-compat: i vecchi URL /:tenant/feed/admin/* redirigono al
                  nuovo prefix sotto messenger così la sidebar messenger resta
                  montata e non smonta il layout durante la navigazione. */}
              <Route path="feed/admin/topics" element={<Navigate to="../messenger/admin/topics" replace />} />
              <Route path="feed/admin/moderation" element={<Navigate to="../messenger/admin/moderation" replace />} />
              <Route path="feed/admin/audit" element={<Navigate to="../messenger/admin/audit" replace />} />
              <Route path="feed/admin/milestones" element={<Navigate to="../messenger/admin/milestones" replace />} />
              <Route path="messenger" element={<ProtectedRoute permission="messenger.access"><MessengerLayout /></ProtectedRoute>}>
                {/* Index → chat: senza questa rotta tappando "Chat" o aprendo
                    /:tenant/messenger si caricava un layout vuoto (pagina bianca). */}
                <Route index element={<Navigate to="chat" replace />} />
                <Route path="feed" element={<FeedPage />} />
                <Route path="chat" element={<MessengerChatPage />} />
                <Route path="gruppi" element={<WorkgroupsListPage />} />
                <Route path="workgroups" element={<WorkgroupsListPage />} />
                <Route path="workgroups/:id" element={<WorkgroupHub />} />
                <Route path="canali" element={<ChannelsPage />} />
                <Route path="admin/topics" element={<Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><FeedTopicsAdminPage /></Suspense>} />
                <Route path="admin/moderation" element={<Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><FeedModerationPage /></Suspense>} />
                <Route path="admin/audit" element={<Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><FeedAuditPage /></Suspense>} />
                <Route path="admin/milestones" element={<Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><FeedMilestonesAdminPage /></Suspense>} />
              </Route>
              <Route path="hr" element={<ProtectedRoute permission="hr.view"><HRPage /></ProtectedRoute>} />
              <Route path="hr/*" element={<ProtectedRoute permission="hr.view"><HRPage /></ProtectedRoute>} />
              <Route path="files" element={<ProtectedRoute permission="file_manager.access"><FileManager /></ProtectedRoute>} />
              <Route path="company-wiki" element={<ProtectedRoute permission="company_wiki.access" moduleKey="company_wiki"><Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><CompanyWiki /></Suspense></ProtectedRoute>} />
              <Route path="company-wiki/*" element={<ProtectedRoute permission="company_wiki.access" moduleKey="company_wiki"><Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}><CompanyWiki /></Suspense></ProtectedRoute>} />
              <Route path="video/:roomId" element={<ProtectedRoute permission="video_call.manage"><VideoCall /></ProtectedRoute>} />
              <Route path="" element={<Navigate to={`/${tenantSlug}/dashboard`} replace />} />
              <Route path="*" element={<Navigate to={`/${tenantSlug}/dashboard`} replace />} />
            </Routes>
            </ErrorBoundary>
          </Layout>
          </SipPhoneProvider>
          </ErrorBoundary>
        } />
        </>
      ) : (
        <Route path="*" element={<Navigate to={`/${tenantSlug}/login`} replace />} />
      )}
    </Routes>
  );
}


function StorageQuotaErrorToastBridge(): JSX.Element | null {
  const { user } = useAuth();
  const navigate = (path: string): void => { window.location.href = path; };
  const slug = (user as { tenant_slug?: string } | null | undefined)?.tenant_slug ?? '';
  return (
    <StorageQuotaErrorToastProvider
      userRole={(user as { role?: string } | null | undefined)?.role ?? null}
      onNavigateDetail={() => navigate(`/${slug}/settings?tab=storage`)}
    />
  );
}

// Path della pagina pubblica embeddabile del widget chatbot. Vive FUORI dalla
// shell autenticata: il visitatore non è loggato, quindi non deve montare
// NotificationHandler (polling `/api/notifications/preferences` → 401), il
// SocketProvider autenticato, né il gestore globale `auth:unauthorized` di
// AuthContext che su un 401 fa "full cleanup + redirect".
const PUBLIC_WIDGET_PATH = '/chatbot/widget/:agentId';

/**
 * Shell completa dell'app autenticata: tutti i provider (auth/socket/module/
 * permission), NotificationHandler e l'intero set di rotte. Montata per ogni
 * path TRANNE la pagina pubblica del widget chatbot.
 */
function AuthenticatedApp() {
  return (
    <TooltipProvider>
    <ShortcutsHelpProvider>
    <AuthProvider>
      <ModuleProvider>
      <PermissionProvider>
      <SocketProvider>
      <div className="min-h-screen bg-background">
        <NotificationHandler />
        <ImpersonationBanner />
        <StorageQuotaErrorToastBridge />
        <Routes>
          {/* Root redirect to demo tenant */}
          <Route path="/" element={<Navigate to="/demo/login" replace />} />

          {/* Task #3602 — /setup/tenant (TenantSetupWizard) dismesso: gli step
              post-creazione (/users, /extensions, /sip-trunks) ignorano il
              tenant_id nel body e creano nel tenant del JWT del chiamante, e
              /freeswitch-deploy/deploy/:id non esiste. Flusso canonico:
              FE master → POST /master/tenants (TenantWizard/CreateTenant). */}

          {/* Video Guest Link - Public redirect page */}
          <Route path="/video/guest/:token" element={<VideoGuestRedirect />} />

          {/* Public booking management page (cancel / reschedule via cancelToken) */}
          <Route path="/booking/manage/:cancelToken" element={<BookingManage />} />
          <Route path="/booking/preview/:slug" element={<BookingPreview />} />

          {/* URL pubblica condivisibile del form: easyflowai.it/:tenantSlug/booking/:slug
              È quella mostrata nelle impostazioni del form ("URL pubblico") e
              va servita prima del catch-all tenant per non finire al dashboard. */}
          <Route path="/:tenantSlug/booking/:slug" element={<BookingPreview />} />

          {/* PWA standalone launcher: il manifest punta qui (start_url=/punto-cassa)
              perché il path POS è dinamico per tenant. Risolve lo slug
              dall'utente loggato/localStorage e redirige a /:slug/punto-cassa. */}
          <Route path="/punto-cassa" element={
            <Suspense fallback={<div className="flex items-center justify-center h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>}>
              <POSLauncher />
            </Suspense>
          } />

          {/* Tenant routes - path-based (easyflowai.it/:tenantSlug/*) */}
          <Route path="/:tenantSlug/*" element={<TenantRoutes />} />

          {/* 404 per path totalmente fuori formato (la validità dello slug
              è già controllata dentro TenantRoutes via useTenantValidation) */}
          <Route path="*" element={<TenantNotFound />} />
        </Routes>
        <Toaster />
        <TenantLimitErrorModal />
      </div>
      </SocketProvider>
      </PermissionProvider>
      </ModuleProvider>
    </AuthProvider>
    </ShortcutsHelpProvider>
    </TooltipProvider>
  );
}

/**
 * Confine pulito tra pagina pubblica del widget e shell autenticata. La pagina
 * pubblica del widget è autonoma (usa solo gli endpoint `/api/chatbot/public/*`
 * via `fetch`) e NON deve montare nessuno dei side-effect da utente loggato.
 */
function AppShell() {
  const location = useLocation();
  const isPublicWidget = matchPath(PUBLIC_WIDGET_PATH, location.pathname) !== null;

  if (isPublicWidget) {
    return (
      <Routes>
        <Route path={PUBLIC_WIDGET_PATH} element={<ChatbotWidget />} />
      </Routes>
    );
  }

  return <AuthenticatedApp />;
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <QueryClientProvider client={queryClient}>
        <AppShell />
      </QueryClientProvider>
    </Router>
  );
}

export default App;